import { useMemo, useState } from 'react';
import { BookMarked, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { explanationOfTerms } from '@/lib/schema/loader';

/**
 * "Explanation of Terms Used Throughout the Scale" — the manual's glossary.
 *
 * 19 Notes for Clarification tell the observer to "see the Explanation of
 * Terms", so this is reachable straight from the NFC dialog rather than
 * buried in a help page. Filterable because "accessible" alone runs several
 * hundred words across three quality levels.
 */
export function GlossaryDialog({
  trigger,
}: {
  trigger?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const glossary = explanationOfTerms();

  const terms = useMemo(() => {
    if (!glossary) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return glossary.terms;
    return glossary.terms.filter(
      (t) =>
        t.term.toLowerCase().includes(needle) ||
        t.definition.toLowerCase().includes(needle),
    );
  }, [glossary, q]);

  if (!glossary) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="gap-2">
            <BookMarked className="h-4 w-4" />
            Explanation of Terms
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden p-0">
        <DialogHeader className="border-b bg-background px-6 py-4">
          <DialogTitle className="font-serif text-lg text-dece-navy">
            {glossary.title}
          </DialogTitle>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter terms…"
              aria-label="Filter glossary terms"
              className="w-full rounded-md border bg-background py-2 pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </DialogHeader>
        <div className="max-h-[calc(85vh-132px)] space-y-5 overflow-y-auto px-6 py-5 text-sm leading-relaxed">
          {terms.length === 0 ? (
            <p className="text-muted-foreground">
              No terms match “{q}”.
            </p>
          ) : (
            terms.map((t) => (
              <section key={t.term}>
                <h3 className="font-serif text-base font-semibold text-dece-navy">
                  {t.term}
                </h3>
                {t.definition
                  .split(/\n{2,}/)
                  .filter(Boolean)
                  .map((para, i) => (
                    <p key={i} className="mt-1 whitespace-pre-line text-foreground/90">
                      {para}
                    </p>
                  ))}
              </section>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
